import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronDown, BookOpen, Download } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * SOP / Help Center for Division Supervisor logins.
 *
 * Written to answer the questions divisional supervisors ask at orientation:
 *  - What does each status mean?
 *  - How do I report a no-show / late arrival / planned absence?
 *  - Where do I see my division's planned absences / lateness?
 *  - Which report does the head office want?
 *
 * Content is editable here in plain JSX — every section is collapsible so
 * the page stays navigable on a phone. No external CMS.
 */
const SECTIONS = [
  {
    id: "overview",
    title: "What This App Does",
    body: (
      <>
        <p>
          The Rolling River Staff Snapshot tracks every staff member's
          attendance for each session of the day (AM, Lunch, PM and any
          extra periods). Each iPad kiosk records sign-ins and sign-outs in
          real time, and your dashboard shows the live picture for your
          division.
        </p>
        <p className="mt-2">
          Your job as a division supervisor: watch the dashboard, pull your
          division reports, look up staff history, and handle the "someone's
          leaving early" case via the kiosk. Adding planned absences /
          latenesses isn't on you — staff submit those themselves, or admin
          handles it.
        </p>
      </>
    ),
  },
  {
    id: "dashboard",
    title: "Reading Your Dashboard",
    body: (
      <>
        <p>
          When you log in, you land on your division's dashboard. At the
          top you'll see six summary cards:
        </p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>
            <strong>Total Expected</strong> — every staff who should be on
            site today (excluding planned absences).
          </li>
          <li>
            <strong>Present</strong> — signed in and on time.
          </li>
          <li>
            <strong>Late</strong> — signed in, but after the cutoff time.
          </li>
          <li>
            <strong>Late Arrival</strong> — staff with a planned late
            start.
          </li>
          <li>
            <strong>Absent</strong> — not signed in by the cutoff and no
            planned absence on file.
          </li>
          <li>
            <strong>Signed Out</strong> — signed in earlier but left
            (early departure).
          </li>
        </ul>
        <p className="mt-2">
          <strong>Tap any card</strong> to filter the staff list below to
          just that status — useful for finding everyone marked absent in
          one click.
        </p>
      </>
    ),
  },
  {
    id: "status-meanings",
    title: "Status Meanings — Cheat Sheet",
    body: (
      <div className="overflow-x-auto">
        <table className="text-sm w-full">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left px-2 py-1.5">Status</th>
              <th className="text-left px-2 py-1.5">What It Means</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            <tr><td className="px-2 py-1.5 font-semibold text-emerald-700">Present</td><td className="px-2 py-1.5">Signed in on time.</td></tr>
            <tr><td className="px-2 py-1.5 font-semibold text-amber-700">Late</td><td className="px-2 py-1.5">Signed in after the cutoff.</td></tr>
            <tr><td className="px-2 py-1.5 font-semibold text-amber-700">Late Arrival</td><td className="px-2 py-1.5">Planned late start — submitted in advance.</td></tr>
            <tr><td className="px-2 py-1.5 font-semibold text-red-700">Absent</td><td className="px-2 py-1.5">Did not sign in, no planned absence on file.</td></tr>
            <tr><td className="px-2 py-1.5 font-semibold text-slate-700">Signed Out</td><td className="px-2 py-1.5">Left early — signed out before EOD.</td></tr>
            <tr><td className="px-2 py-1.5 font-semibold text-slate-500">Pending</td><td className="px-2 py-1.5">Not yet signed in — still within the on-time window.</td></tr>
            <tr><td className="px-2 py-1.5 font-semibold text-violet-700">Planned Absent</td><td className="px-2 py-1.5">Approved day off submitted in advance.</td></tr>
          </tbody>
        </table>
      </div>
    ),
  },
  {
    id: "reports",
    title: "Pulling Reports for Your Division",
    body: (
      <>
        <p>Two buttons on your dashboard generate division-scoped reports:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>
            <strong>Planned Absences</strong> (amber) — opens a grid of
            every staff in your division with planned absences on file.
            Click the button to view in a new tab, or click the download
            icon for the Excel.
          </li>
          <li>
            <strong>Planned Lateness</strong> (orange) — same layout, but
            for late arrivals.
          </li>
        </ul>
        <p className="mt-2">
          Both views are <strong>scoped to your division only</strong> —
          admins see everyone, but you only see your team. Both work on
          mobile (cards) and desktop (grid).
        </p>
      </>
    ),
  },
  {
    id: "staff-cards",
    title: "Looking Up a Specific Staff Member",
    body: (
      <>
        <p>
          The <strong>Staff Cards</strong> button (top of the dashboard)
          opens a searchable list of every staff member in your division.
          Tap a name to see:
        </p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Their full sign-in history (every day, every period).</li>
          <li>Payroll detail (hours worked, planned days off).</li>
          <li>Photo + contact info pulled from CampMinder.</li>
          <li>Any planned absences / latenesses already on file.</li>
        </ul>
        <p className="mt-2 text-sm text-slate-500">
          Need to bulk-search across the entire camp? Ask admin —
          division-level access is limited to your team for privacy.
        </p>
      </>
    ),
  },
  {
    id: "leaving-early",
    title: "When a Staff Member Leaves Early",
    body: (
      <>
        <p>
          Send them to the iPad kiosk to sign out <em>before</em> they
          leave. Their status flips to <strong>Signed Out</strong> on your
          dashboard within seconds.
        </p>
        <p className="mt-2">
          If they forgot and already left, ask admin to sign them out from
          their staff card. Don't leave them showing as Present — payroll
          uses that number.
        </p>
      </>
    ),
  },
  {
    id: "issues",
    title: "Issues?",
    body: (
      <p>If you have an issue, let Brian know.</p>
    ),
  },
];

function Accordion({ id, title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`help-section-${id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left active:bg-slate-50"
      >
        <span className="font-display font-bold text-slate-900 text-base sm:text-lg">{title}</span>
        <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 text-sm text-slate-700 leading-relaxed border-t border-slate-100">
          {children}
        </div>
      )}
    </section>
  );
}

export default function Help() {
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);

  const onDownloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/help/division-login-guide.pdf", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "RRDC_Staff_Snapshot_Division_Login_Guide.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              onClick={() => navigate("/dashboard")}
              className="btn-touch inline-flex items-center gap-1 px-2 sm:px-3 rounded-lg text-slate-700 hover:bg-slate-100 shrink-0"
              data-testid="help-back"
            >
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
            </button>
            <img src="/logo.png" alt="Rolling River Day Camp" className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl object-contain bg-white shadow-sm p-0.5 shrink-0" />
            <div className="min-w-0 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-emerald-600 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Help & SOP</div>
                <h1 className="font-display text-base sm:text-lg font-bold text-slate-900">Division Supervisor Guide</h1>
              </div>
            </div>
          </div>
          <button
            onClick={onDownloadPdf}
            disabled={downloading}
            className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold text-sm shrink-0"
            data-testid="help-download-pdf"
            title="Download printable PDF guide"
          >
            <Download className="w-4 h-4" /> {downloading ? "…" : "PDF"}
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-3">
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 px-4 py-3 text-sm">
          Quick reference for division supervisors. Tap any section to
          expand. For anything not covered here, ask the app admin.
        </div>
        {SECTIONS.map((s, idx) => (
          <Accordion key={s.id} id={s.id} title={s.title} defaultOpen={idx < 2}>
            {s.body}
          </Accordion>
        ))}
        <div className="text-[11px] text-slate-400 text-center pt-4">
          Rolling River Day Camp · Staff Snapshot · SOP rev 1.1
        </div>
      </main>
    </div>
  );
}
